import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FilterChips, KpiTile, RecordsState, SearchField } from "./shared";
import { useStyles as useNegocioStyles } from "./styles";
import { money } from "./helpers";
import { statusLabel } from "../Storefront/helpers";
import { OriginBadge, fmtDate } from "./Orders";

/**
 * Historial de Negocio › Pedidos: todo lo vendido (tienda web, bots, agente IA, panel y ventas
 * registradas a mano) y los clientes que compraron, con filtros por origen y periodo.
 */

const useStyles = makeStyles((theme) => ({
  toolbar: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", margin: "8px 0 14px" },
  list: { display: "grid", gap: 8 },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(170px, auto) 1fr auto",
    gap: 14,
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  main: { minWidth: 0, display: "grid", gap: 2, "& strong": { fontSize: 14 }, "& span": { fontSize: 12.5, color: theme.palette.text.secondary } },
  side: { display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end", alignItems: "center" },
  amount: { fontWeight: 700, fontSize: 15, marginRight: 6 },
  originGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10, margin: "4px 0 14px" },
  originCard: {
    display: "grid",
    gap: 6,
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: "pointer",
    fontSize: 13,
    "& b": { fontSize: 16 },
    "&[aria-pressed='true']": { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 1px ${theme.palette.primary.main}` }
  },
  top: { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", margin: "0 0 14px", fontSize: 13 },
  more: { display: "flex", justifyContent: "center", marginTop: 14 }
}));

const SCOPES = [
  { value: "sold", label: "Vendidos" },
  { value: "pending", label: "Pendientes" },
  { value: "cancelled", label: "Cancelados" },
  { value: "all", label: "Todos" }
];

export const HISTORY_ORIGINS = [
  { value: "", label: "Todos los orígenes" },
  { value: "web", label: "Tienda web" },
  { value: "bot", label: "Bot (WhatsApp / Telegram)" },
  { value: "agent", label: "Agente IA" },
  { value: "panel", label: "Panel" },
  { value: "manual", label: "Registro manual" }
];

const PERIODS = [
  { value: "month", label: "Este mes" },
  { value: "lastMonth", label: "Mes pasado" },
  { value: "7d", label: "Últimos 7 días" },
  { value: "30d", label: "Últimos 30 días" },
  { value: "year", label: "Este año" },
  { value: "all", label: "Todo el historial" }
];

const CUSTOMER_SORTS = [
  { value: "last", label: "Compra más reciente" },
  { value: "total", label: "Mayor total" },
  { value: "count", label: "Más compras" }
];

/** Rango [from, to) en hora local del navegador, enviado como ISO (UTC). */
export const periodRange = (period, now = new Date()) => {
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const tomorrow = new Date(day(now).getTime() + 86400000);
  switch (period) {
    case "month": return { from: new Date(y, m, 1), to: new Date(y, m + 1, 1) };
    case "lastMonth": return { from: new Date(y, m - 1, 1), to: new Date(y, m, 1) };
    case "7d": return { from: new Date(tomorrow.getTime() - 7 * 86400000), to: tomorrow };
    case "30d": return { from: new Date(tomorrow.getTime() - 30 * 86400000), to: tomorrow };
    case "year": return { from: new Date(y, 0, 1), to: new Date(y + 1, 0, 1) };
    default: return { from: null, to: null };
  }
};

const ORIGIN_NAMES = { web: "Tienda web", bot: "Bot", agent: "Agente IA", panel: "Panel", manual: "Registro manual" };
const historyStatusLabel = (s) => (s === "pending_payment" ? "Pendiente de pago" : s === "paid" ? "Pagada" : statusLabel(s));
const statusTone = (s) =>
  ["paid", "fulfilled"].includes(s) ? "#16a34a" : s === "manual_activation" ? "#7c3aed" : ["cancelled", "expired"].includes(s) ? "#94a3b8" : "#d97706";
const rowMoney = (r) => money(r.amount, r.currency && r.currency !== "local" ? r.currency : undefined);

export default function OrdersHistory({ mode, onOpenOrder, onOpenChat, onShowCustomer, presetQ }) {
  const c = useStyles();
  const negocio = useNegocioStyles();
  const [scope, setScope] = useState("sold");
  const [origin, setOrigin] = useState("");
  const [period, setPeriod] = useState("month");
  const [sort, setSort] = useState("last");
  const [q, setQ] = useState(presetQ || "");
  const [data, setData] = useState({ records: [], count: 0, hasMore: false, summary: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (presetQ != null) setQ(presetQ); }, [presetQ]);

  const params = useMemo(() => {
    const { from, to } = periodRange(period);
    return { status: scope, origin, q, sort, from: from ? from.toISOString() : undefined, to: to ? to.toISOString() : undefined };
  }, [scope, origin, q, sort, period]);
  const current = useRef(params);
  current.current = params;

  const load = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const url = mode === "customers" ? "/negocio/orders/history/customers" : "/negocio/orders/history";
      const { data: res } = await api.get(url, { params: { ...current.current, page: nextPage } });
      setData((prev) => (nextPage > 1 && prev.mode === mode ? { ...prev, ...res, mode, records: [...prev.records, ...res.records], summary: prev.summary || res.summary } : { ...res, mode }));
      setPage(nextPage);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    const timer = setTimeout(() => load(1), q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [params, load, q]);

  // Al cambiar de vista, los registros de la otra vista no se pintan con este formato.
  const fresh = data.mode === mode;
  const summary = fresh ? data.summary : null;
  const records = fresh ? data.records || [] : [];

  return (
    <>
      <div className={c.toolbar}>
        <FilterChips value={scope} onChange={setScope} options={SCOPES} />
        <TextField select size="small" variant="outlined" label="Periodo" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ minWidth: 170 }}>
          {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label="Origen" value={origin} onChange={(e) => setOrigin(e.target.value)} style={{ minWidth: 200 }}>
          {HISTORY_ORIGINS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {mode === "customers" && (
          <TextField select size="small" variant="outlined" label="Ordenar" value={sort} onChange={(e) => setSort(e.target.value)} style={{ minWidth: 180 }}>
            {CUSTOMER_SORTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
        <SearchField value={q} onChange={setQ} placeholder={mode === "customers" ? "Buscar cliente, número o servicio" : "Buscar cliente, número, servicio o #"} />
      </div>

      {mode !== "customers" && summary && (
        <>
          <div className={negocio.kpiGrid}>
            <KpiTile icon={ShoppingCartOutlinedIcon} label={scope === "sold" ? "Ventas" : "Registros"} value={summary.count} />
            <KpiTile icon={AttachMoneyIcon} label="Total" value={money(summary.total)} />
            <KpiTile icon={PeopleOutlineIcon} label="Clientes" value={summary.customers} />
          </div>
          {summary.byOrigin.length > 0 && (
            <div className={c.originGrid}>
              {summary.byOrigin.slice().sort((a, b) => b.count - a.count).map((o) => (
                <div
                  key={o.origin}
                  role="button"
                  tabIndex={0}
                  aria-pressed={origin === o.origin}
                  className={c.originCard}
                  onClick={() => setOrigin(origin === o.origin ? "" : o.origin)}
                  onKeyDown={(e) => { if (e.key === "Enter") setOrigin(origin === o.origin ? "" : o.origin); }}
                >
                  <div><OriginBadge order={{ origin: o.origin, channel: o.origin === "bot" ? "whatsapp" : o.origin === "web" ? "web" : null }} /></div>
                  <div><b>{o.count}</b> {o.count === 1 ? "venta" : "ventas"} · {money(o.total)}</div>
                  <div>{o.customers} {o.customers === 1 ? "cliente" : "clientes"}</div>
                </div>
              ))}
            </div>
          )}
          {summary.topProducts.length > 0 && (
            <div className={c.top}>
              <strong>Más vendidos:</strong>
              {summary.topProducts.map((p) => (
                <Chip key={p.name} size="small" variant="outlined" clickable label={`${p.name} · ${p.count}`} onClick={() => setQ(p.name)} />
              ))}
            </div>
          )}
        </>
      )}

      {mode === "customers" ? (
        <RecordsState loading={loading || !fresh} count={records.length} emptyTitle="No hay clientes con estos filtros" emptySubtitle="Aquí aparece cada cliente que compró en el periodo, venga de la tienda, los bots, el agente o un registro manual.">
          <p style={{ fontSize: 13, margin: "0 0 10px" }}>{data.count} {data.count === 1 ? "cliente" : "clientes"}</p>
          <div className={c.list}>
            {records.map((r) => (
              <article key={r.contactId} className={c.row}>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {r.origins.map((o) => <OriginBadge key={o} order={{ origin: o, channel: o === "bot" ? "whatsapp" : o === "web" ? "web" : null }} />)}
                </div>
                <div className={c.main}>
                  <strong>{r.name || "Cliente"}{r.number ? ` · ${r.number}` : ""}</strong>
                  <span>{r.products || "—"}</span>
                  <span>Primera compra {fmtDate(r.firstAt)} · última {fmtDate(r.lastAt)}</span>
                </div>
                <div className={c.side}>
                  <span className={c.amount}>{money(r.total)}</span>
                  <Chip size="small" label={`${r.purchases} ${r.purchases === 1 ? "compra" : "compras"}`} />
                  <Button size="small" onClick={() => onShowCustomer(r)}>Ver compras</Button>
                  {r.contactId && <Button size="small" onClick={() => onOpenChat(r)}>Abrir chat</Button>}
                </div>
              </article>
            ))}
          </div>
        </RecordsState>
      ) : (
        <RecordsState loading={loading || !fresh} count={records.length} emptyTitle="No hay ventas con estos filtros" emptySubtitle="Aquí queda todo lo vendido: tienda web, bots, agente IA, panel y ventas registradas a mano.">
          <div className={c.list}>
            {records.map((r) => (
              <article key={r.key} className={c.row}>
                <div>
                  {r.origin === "manual" && r.registeredBy
                    ? <Tooltip title={`Registrada por ${r.registeredBy}`}><span><OriginBadge order={r} /></span></Tooltip>
                    : <OriginBadge order={r} />}
                </div>
                <div className={c.main}>
                  <strong>{r.type === "order" ? `Pedido #${r.id}` : `Venta #${r.id}`} · {r.title}</strong>
                  <span>{r.name || "Cliente"}{r.number ? ` · ${r.number}` : ""}{r.paymentMethod ? ` · ${r.paymentMethod}` : ""}</span>
                  <span>
                    {fmtDate(r.createdAt)}
                    {r.renewal && r.type !== "order" ? " · renovación" : ""}
                    {r.type === "order" && r.saleId ? ` · venta #${r.saleId}` : ""}
                    {r.deliveredManually ? " · entregado a mano" : ""}
                    {r.origin === "manual" && r.registeredBy ? ` · por ${r.registeredBy}` : ""}
                  </span>
                </div>
                <div className={c.side}>
                  <span className={c.amount}>{rowMoney(r)}</span>
                  <Chip size="small" label={historyStatusLabel(r.status)} style={{ background: statusTone(r.status), color: "#fff" }} />
                  {r.type === "order" && <Button size="small" onClick={() => onOpenOrder(r.id)}>Ver</Button>}
                  {r.contactId && <Button size="small" onClick={() => onOpenChat(r)}>Chat</Button>}
                </div>
              </article>
            ))}
          </div>
        </RecordsState>
      )}
      {fresh && data.hasMore && (
        <div className={c.more}><Button variant="outlined" disabled={loading} onClick={() => load(page + 1)}>Cargar más</Button></div>
      )}
      {mode !== "customers" && origin && (
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 10 }}>Filtrado por {ORIGIN_NAMES[origin] || origin}.</p>
      )}
    </>
  );
}
